import { PropType, computed } from 'vue'
import { pathWithQueryParams } from '@/utilities/path'
import htmlUtil from '@/utilities/html'

export const DEFAULT_IMAGE_OPTIONS = {
  WIDTH: {
    PC: 200,
    SP: 140,
  },
  HEIGHT: {
    PC: 100,
    SP: 70,
  },
} as const

export const DEFAULT_HEADING_OPTIONS = {
  TAG_NAME: 'p',
  LINE_HEIGHT: 1.4,
  FONT_SIZE: {
    PC: '1.6rem',
    SP: '1.6rem',
  },
  FONT_WEIGHT: 'normal',
  MAX_LINE: 0,
} as const

export const cardCommonProps = {
  /**
   * 画像を表示するかどうか
   */
  hasHero: {
    type: Boolean,
    default: true,
  },

  /**
   * ハイライト表示するワード
   */
  highlightWord: {
    type: String,
    default: '',
  },

  size: {
    type: String as PropType<'full' | 'large' | 'normal' | 'small' | 'mini'>,
    default: 'normal',
    validator: (value: string) => {
      return ['full', 'large', 'normal', 'small', 'mini'].includes(value)
    },
  },

  /**
   * PC でも横向きタイプ表示にするフラグ
   * @description 重複定義の場合は full の設定が優先される
   */
  horizontal: {
    type: Object as PropType<{ pc: boolean, sp: boolean }>,
    default: () => ({
      pc: false,
      sp: false,
    }),
  },

  /**
   * 見出しのオプション
   */
  headingOptions: {
    type: Object as PropType<{
      // HTML 要素名
      tagName?: keyof HTMLElementTagNameMap,

      // CSS の line-height 値
      lineHeight?: number,

      // CSS の font-size
      fontSize?: {
        pc: string,
        sp: string,
      },

      // 文字の太さ
      fontWeight?: 'normal' | 'bold',

      // 表示最大行数
      maxLine?: number,
    }>,
    default: () => ({
      tagName: DEFAULT_HEADING_OPTIONS.TAG_NAME,
      lineHeight: DEFAULT_HEADING_OPTIONS.LINE_HEIGHT,
      fontSize: {
        pc: DEFAULT_HEADING_OPTIONS.FONT_SIZE.PC,
        sp: DEFAULT_HEADING_OPTIONS.FONT_SIZE.SP,
      },
      fontWeight: DEFAULT_HEADING_OPTIONS.FONT_WEIGHT,
      maxLine: DEFAULT_HEADING_OPTIONS.MAX_LINE,
    }),
  },

  /**
   * 画像の表示オプション
   * @description horizontal が有効のときのみ評価される
   */
  imageOptions: {
    type: Object as PropType<{
      // 幅
      width?: {
        pc: number,
        sp: number,
      },

      // 高さ
      height?: {
        pc: number,
        sp: number,
      },
    }>,
    default: () => ({
      width: {
        pc: DEFAULT_IMAGE_OPTIONS.WIDTH.PC,
        sp: DEFAULT_IMAGE_OPTIONS.WIDTH.SP,
      },
      height: {
        pc: DEFAULT_IMAGE_OPTIONS.HEIGHT.PC,
        sp: DEFAULT_IMAGE_OPTIONS.HEIGHT.SP,
      },
    }),
  },
}

export const defaultProps = {
  item: {
    type: Object as PropType<ContentItem>,
    required: true,
  },

  ...cardCommonProps,
}

export interface Props {
  item: ContentItem,
  hasHero: boolean,
  highlightWord: string,
  size: 'full' | 'large' | 'normal' | 'small' | 'mini',
  horizontal: {
    pc: boolean,
    sp: boolean,
  },
  headingOptions: {
    tagName?: keyof HTMLElementTagNameMap,
    lineHeight?: number,
    fontSize?: {
      pc: string,
      sp: string,
    },
    fontWeight?: 'normal' | 'bold',
    maxLine?: number,
  },
  imageOptions: {
    width?: {
      pc: number,
      sp: number,
    },
    height?: {
      pc: number,
      sp: number,
    },
  },
}
export function defaultFunction(props: any & Props) {
  const sizeStateClass = computed<string>(() => {
    return `is-${props.size}`
  })

  const linkTo = computed<string | undefined>(() => {
    return props.item.to
      ? pathWithQueryParams(props.item.to, props.item.parameters)
      : undefined
  })

  /**
   * 見出しの中で、指定のワードをハイライト表示する
   */
  const highlightHeading = computed<string>(() => {
    if (!props.item.title) return ''

    const title = htmlUtil.removeHtmlElement(props.item.title)
    return props.highlightWord
      ? title.replace(new RegExp(`(${props.highlightWord})`, 'g'), '<span class="u-text__highlight">$1</span>')
      : title
  })

  /**
   * 説明文の中で、指定のワードをハイライト表示する
   */
  const highlightDescription = computed<string>(() => {
    if (!props.item.description) return ''

    const description = htmlUtil.removeHtmlElement(props.item.description)
    return props.highlightWord
      ? description.replace(new RegExp(`(${props.highlightWord})`, 'g'), '<span class="u-text__highlight">$1</span>')
      : description
  })

  const wrapperTagName = computed<string>(() => {
    return props.item.to ? 'a' : 'div'
  })

  /**
   * 見出し部分の html とスタイルに関連する値
   */
  const computedHeadingOptions = computed<{
    tagName: keyof HTMLElementTagNameMap,
    maxHeight: string,
    lineHeight: number,
    fontSize: {
      pc: string,
      sp: string,
    },
    fontWeight: 'normal' | 'bold',
  }>(() => {
    // デフォルト値をベースに分割代入で props で渡された値をオーバーライド
    const _options = {
      ...{
        tagName: DEFAULT_HEADING_OPTIONS.TAG_NAME,
        lineHeight: DEFAULT_HEADING_OPTIONS.LINE_HEIGHT,
        fontSize: {
          pc: DEFAULT_HEADING_OPTIONS.FONT_SIZE.PC,
          sp: DEFAULT_HEADING_OPTIONS.FONT_SIZE.SP,
        },
        fontWeight: DEFAULT_HEADING_OPTIONS.FONT_WEIGHT,
        maxLine: DEFAULT_HEADING_OPTIONS.MAX_LINE,
      },
      ...props.headingOptions,
    }

    const _maxLine = _options.maxLine > 0
      ? _options.maxLine
      : DEFAULT_HEADING_OPTIONS.MAX_LINE
    const _lineHeight = _options.lineHeight > 0
      ? _options.lineHeight
      : DEFAULT_HEADING_OPTIONS.LINE_HEIGHT
    const _maxHeight = _maxLine * _lineHeight
    const _fontSize = {
      pc: _options.fontSize.pc || DEFAULT_HEADING_OPTIONS.FONT_SIZE.PC,
      sp: _options.fontSize.sp || DEFAULT_HEADING_OPTIONS.FONT_SIZE.SP,
    }
    const _fontWeight = _options.fontWeight || 'normal'

    return {
      tagName: _options.tagName || DEFAULT_HEADING_OPTIONS.TAG_NAME,
      maxHeight: _maxHeight ? `${_maxHeight}em` : 'none',
      lineHeight: _lineHeight,
      fontSize: _fontSize,
      fontWeight: _fontWeight,
    }
  })

  /**
   * 画像部分のスタイルに関連する値
   */
  const computedImageOptions = computed<{
    width: { pc: string, sp: string },
    height: { pc: string, sp: string }
  }>(() => {
    // デフォルト値をベースに分割代入で props で渡された値をオーバーライド
    const _options = {
      ...{
        width: {
          pc: DEFAULT_IMAGE_OPTIONS.WIDTH.PC,
          sp: DEFAULT_IMAGE_OPTIONS.WIDTH.SP,
        },
        height: {
          pc: DEFAULT_IMAGE_OPTIONS.HEIGHT.PC,
          sp: DEFAULT_IMAGE_OPTIONS.HEIGHT.SP,
        },
      },
      ...props.imageOptions,
    }

    const _width = {
      pc: _options.width.pc >= 0
        ? `${_options.width.pc}px`
        : `${DEFAULT_IMAGE_OPTIONS.WIDTH.PC}px`,
      sp:  _options.width.sp >= 0
        ? `${_options.width.sp}px`
        : `${DEFAULT_IMAGE_OPTIONS.WIDTH.SP}px`,
    }

    const _height = {
      pc: _options.height.pc >= 0
        ? props.horizontal.pc
          ? `${_options.height.pc}px`
          : `${_options.height.pc / _options.width.pc * 100}%`
        : `${DEFAULT_IMAGE_OPTIONS.HEIGHT.PC}px`,
      sp:  _options.height.sp >= 0
        ? props.horizontal.sp
          ? `${_options.height.sp}px`
          : `${_options.height.sp / _options.width.sp * 100}%`
        : `${DEFAULT_IMAGE_OPTIONS.HEIGHT.SP}px`,
    }

    return {
      width: _width,
      height: _height,
    }
  })

  /**
   * 画像のサイズを外から渡すために CSS 変数として style 属性でバインドするためのオブジェクト
   *
   * @description 値は **単位なし数値** として渡す。iOS Safari の calc() が単位つきの「px ÷ px * 100%」をサポートしないため、
   *              CSS 変数自体は単位なしで保持し、SASS 側で必要に応じて `px()` / `rate()` ヘルパー（_functions.sass）を通して
   *              px / % に変換する運用とする。
   *              `--image-aspect-*` は height / width の比率（幅に対する高さの相対値）を示す unitless 数値（例: 0.5）。
   */
  const cssVariables = computed<Record<string, string>>(() => {
    const _options = {
      ...{
        width: {
          pc: DEFAULT_IMAGE_OPTIONS.WIDTH.PC,
          sp: DEFAULT_IMAGE_OPTIONS.WIDTH.SP,
        },
        height: {
          pc: DEFAULT_IMAGE_OPTIONS.HEIGHT.PC,
          sp: DEFAULT_IMAGE_OPTIONS.HEIGHT.SP,
        },
      },
      ...props.imageOptions,
    }

    const widthPc  = _options.width.pc  >= 0 ? _options.width.pc  : DEFAULT_IMAGE_OPTIONS.WIDTH.PC
    const widthSp  = _options.width.sp  >= 0 ? _options.width.sp  : DEFAULT_IMAGE_OPTIONS.WIDTH.SP
    const heightPc = _options.height.pc >= 0 ? _options.height.pc : DEFAULT_IMAGE_OPTIONS.HEIGHT.PC
    const heightSp = _options.height.sp >= 0 ? _options.height.sp : DEFAULT_IMAGE_OPTIONS.HEIGHT.SP

    return {
      '--image-width-pc':  `${widthPc}`,
      '--image-height-pc': `${heightPc}`,
      '--image-aspect-pc': `${heightPc / widthPc}`,
      '--image-width-sp':  `${widthSp}`,
      '--image-height-sp': `${heightSp}`,
      '--image-aspect-sp': `${heightSp / widthSp}`,
    }
  })

  return {
    // computed
    sizeStateClass,
    linkTo,
    highlightHeading,
    highlightDescription,
    wrapperTagName,
    computedHeadingOptions,
    computedImageOptions,
    cssVariables,
  }
}
